/**
 * Unified Application Routes
 *
 * This component replaces the individual admin route files:
 * - PlatformAdmin.tsx
 * - MainAgencyAdmin.tsx
 * - MspAgencyAdmin.tsx
 * - AgencyAdmin.tsx
 * - CompanyAdmin.tsx
 *
 * Routes are rendered based on:
 * 1. User's role (determines layout variant)
 * 2. Backend permissions (from Redux store)
 * 3. Route configuration (from routeConfig.ts)
 *
 * @see routeConfig.ts for route definitions
 * @see PermissionWrapper.tsx for permission checking
 */

import React, {lazy, Suspense, useEffect, useState} from 'react'
import {Navigate, Route, Routes, useNavigate} from 'react-router-dom'
import Layout from '../components/layout/Layout'
import S from '../components/common/styles/AdminStyles'
import {useGlobalSnackbar} from '../context/GlobalSnackbarContext'
import {TCognitoRole} from '../models/role'
import {userApi} from '../services/userApi'
import {useLazyGetUserByEmailQuery} from '../store/apis/user-api'
import {useSignoutMutation} from '../store/apis/auth-api'
import {notificationsApi} from '../store/apis/notifications-api'
import {updateCurrentUser} from '../store/slices/root-slice'
import {useAppDispatch, useAppSelector} from '../store/store-hooks'
import {isEnabled} from '~/src/featureFlags'
import EulaDialog from '../pages/sign-in/EulaDialog'
import RouteNotFound from './RouteNotFound'
import {BRANCH_LINK_ROUTE_IDS, BranchFromLinkGate} from './BranchFromLinkGate'
import {
  LayoutConfig,
  LayoutVariant,
  RouteConfig,
  PLATFORM_ADMIN_CONFIG,
  MAIN_AGENCY_ADMIN_CONFIG,
  MSP_AGENCY_ADMIN_CONFIG,
  AGENCY_ADMIN_CONFIG,
  COMPANY_ADMIN_CONFIG,
  CANDIDATE_CONFIG,
  passesPlatformAdminGate,
} from './routeConfig'
import LoadingBoundary from '@ui/loader'
import {ChunkErrorBoundary} from '@ui/error'

// Settings Redirect - redirects to the first available settings submodule based on permissions
import SettingsRedirectComponent from '../components/platform/settings/SettingsRedirect'
import {reportCatchError} from '../utils/errorReporter'
import {isRouteDeniedByPermissions} from './routeAccess'

// ============================================================================
// LAZY LOADED COMPONENTS
// ============================================================================
// Note: Components with named exports use .then() to provide default export

// Dashboard Components
const Dashboard = lazy(() => import('../components/platform/dashboard'))
const AgencyDashboard = lazy(() => import('../components/nonmsp/Dashboard/Dashboard'))
const CompanyDashboard = lazy(() => import('../components/company/dashboard'))

// Company Management
const ManageCompaniesPage = lazy(() =>
  import('../components/platform/company/ManageCompaniesPage').then((m) => ({
    default: m.ManageCompaniesPage,
  })),
)
const CompanyManagementPage = lazy(() =>
  import('../components/platform/company/CompanyManagementPage').then((m) => ({
    default: m.CompanyManagementPage,
  })),
)
const CompanyDetailPage = lazy(() =>
  import('../components/platform/company/CompanyDetailPage').then((m) => ({
    default: m.CompanyDetailPage,
  })),
)
const AddNewPositionPage = lazy(() =>
  import('../components/platform/company/AddNewPositionPage').then((m) => ({
    default: m.AddNewPositionPage,
  })),
)
const EditPositionPage = lazy(() =>
  import('../components/platform/company/EditPositionPage').then((m) => ({
    default: m.EditPositionPage,
  })),
)
const AddPositionTemplatePage = lazy(() =>
  import('../components/platform/company/AddPositionTemplatePage').then((m) => ({
    default: m.AddPositionTemplatePage,
  })),
)

// Agency Management
const ManageAgenciesPage = lazy(() =>
  import('../components/platform/agency/ManageAgenciesPage').then((m) => ({
    default: m.ManageAgenciesPage,
  })),
)
const AgencyManagementPage = lazy(() =>
  import('../components/platform/agency/AgencyManagementPage').then((m) => ({
    default: m.AgencyManagementPage,
  })),
)
const OnboardingAgency = lazy(() => import('../components/platform/onboarding/OnboardingAgency'))
const OnboardingCompany = lazy(() => import('../components/platform/onboarding/OnboardingCompany'))

// Global Routes
const CompensationCodesPage = lazy(() =>
  import('../components/platform/global/compensation-codes/CompensationCodesPage').then((m) => ({
    default: m.CompensationCodesPage,
  })),
)
const WCCatalogsPage = lazy(() =>
  import('../components/platform/global/wc-catalogs/WCCatalogsPage').then((m) => ({
    default: m.WCCatalogsPage,
  })),
)
const CatalogCompensationCodesPage = lazy(() =>
  import('../components/platform/global/wc-catalogs/CatalogCompensationCodesPage').then((m) => ({
    default: m.CatalogCompensationCodesPage,
  })),
)
const PaycodesPage = lazy(() =>
  import('../components/platform/global/paycodes/PaycodesPageClean').then((m) => ({
    default: m.PaycodesPage,
  })),
)
const PaycodeFormPage = lazy(() =>
  import('../components/platform/global/paycodes/PaycodeFormPage').then((m) => ({
    default: m.PaycodeFormPage,
  })),
)
const AuthoritiesPage = lazy(() =>
  import('../components/platform/global/authorities/AuthoritiesPage').then((m) => ({
    default: m.AuthoritiesPage,
  })),
)
const TaxManagementPage = lazy(() =>
  import('../components/platform/global/tax/TaxManagementPage').then((m) => ({
    default: m.TaxManagementPage,
  })),
)
const MetricsDashboardPage = lazy(() =>
  import('../components/platform/global/metrics').then((m) => ({
    default: m.MetricsDashboardPage,
  })),
)
const ZoomConfigPage = lazy(() =>
  import('../components/platform/global/zoom/ZoomConfigPage').then((m) => ({
    default: m.ZoomConfigPage,
  })),
)
const LenderAdminPage = lazy(() =>
  import('../components/platform/global/lenders/LenderAdminPage').then((m) => ({
    default: m.LenderAdminPage,
  })),
)
const FinancingSchedulesPage = lazy(() =>
  import('../components/platform/global/financing-schedules/FinancingSchedulesPage').then((m) => ({
    default: m.FinancingSchedulesPage,
  })),
)
const TimeOffPoliciesPage = lazy(() =>
  import('../components/platform/global/time-off/TimeOffPoliciesPage').then((m) => ({
    default: m.TimeOffPoliciesPage,
  })),
)
const TimeOffCategoryDetailPage = lazy(() =>
  import('../components/platform/global/time-off/TimeOffCategoryDetailPage').then((m) => ({
    default: m.TimeOffCategoryDetailPage,
  })),
)
const CreateTimeOffPolicyPage = lazy(() =>
  import('../components/platform/global/time-off/CreateTimeOffPolicyPage').then((m) => ({
    default: m.CreateTimeOffPolicyPage,
  })),
)
const TimeOffPolicyDetailPage = lazy(() =>
  import('../components/platform/global/time-off/TimeOffPolicyDetailPage').then((m) => ({
    default: m.TimeOffPolicyDetailPage,
  })),
)
const PlaceholderPage = lazy(() => import('../components/platform/global/PlaceholderPage'))
const ActivityLogsPage = lazy(() =>
  import('../components/activity-logs').then((m) => ({
    default: m.ActivityLogsPage,
  })),
)

// Payroll
const PayrollManagementPage = lazy(
  () => import('../components/platform/payroll/PayrollManagementPage'),
)
const PayrollReportsPage = lazy(() =>
  import('../components/platform/reports').then((m) => ({default: m.PayrollReportsPage})),
)
// Finance (KRAF-763)
const FinancePage = lazy(() => import('../components/platform/finance/FinancePage'))
const BillingSettingsPage = lazy(
  () => import('../components/platform/payroll/views/BillingSettingsPage'),
)

// Service Orders
const NewServiceOrderFormPage = lazy(
  () => import('../components/company/manage-orders/NewServiceOrderFormPage'),
)
const ServiceOrderDetailsPage = lazy(() =>
  import('../components/common/service-orders').then((m) => ({
    default: m.ServiceOrderDetailsPage,
  })),
)
const ServiceOrderSummary = lazy(
  () =>
    import(
      '../components/company/manage-orders/manage-order-tables/company-summary/ServiceOrderSummary'
    ),
)
const AgencyManageOrdersMain = lazy(
  () => import('../components/msp/manage-orders/agency-order-tables/AgencyManageOrdersMain'),
)
const AgencyPendingOrders = lazy(
  () => import('../components/msp/manage-orders/agency-order-tables/AgencyPendingOrders'),
)
const CompletedOrdersMain = lazy(
  () => import('../components/msp/manage-orders/completed-orders/CompletedOrdersMain'),
)
const DeletedOrdersMain = lazy(
  () => import('../components/msp/manage-orders/deleted-orders/DeletedOrdersMain'),
)
const ManageOrders = lazy(
  () => import('../components/company/manage-orders/manage-order-tables/ManageOrders'),
)
const OrderDetails = lazy(
  () => import('../components/company/manage-orders/manage-order-tables/OrderDetails'),
)
const CompanyCompletedOrdersMain = lazy(
  () => import('../components/company/manage-orders/completed-orders/CompanyCompletedOrdersMain'),
)
const CompanyCandidateViewMain = lazy(
  () =>
    import('../components/company/manage-orders/company-candidate-view/CompanyCandidateViewMain'),
)

// Job Orders
const MspAgencyJobOrderMain = lazy(
  () => import('../components/msp/manage-job-orders/job-orders-main/MspJobOrdersMain'),
)
const MspJobOrdersDetailsMain = lazy(
  () => import('../components/msp/manage-job-orders/job-order-details/MspJobOrdersDetailsMain'),
)
const JobOrdersDetailsSummaryMain = lazy(
  () => import('../components/common/job-orders/JobOrderDetailsSummaryMain'),
)
const JobOrderFromPrefill = lazy(
  () => import('../components/msp/manage-job-orders/job-order-summary/JobOrderFromPrefill'),
)
const SubOrderDetailsPage = lazy(
  () => import('../components/common/job-orders/backup-order/SubOrderDetailsPage'),
)

// Candidates & Prefill
const Candidates = lazy(() =>
  import('../components/common/candidates/Candidates').then((m) => ({default: m.Candidates})),
)
const PrefillMain = lazy(() => import('../components/common/prefill/PrefillMain'))

// Requests (centralized Time Off review)
const RequestsPage = lazy(() => import('../components/common/requests/RequestsPage'))
const AddOrEditPrefill = lazy(() => import('../components/common/prefill/AddOrEditPrefill'))

// Data Management
const ImportWizardMain = lazy(() =>
  import('../components/common/import-wizard/ImportWizardMain').then((m) => ({
    default: m.ImportWizardMain,
  })),
)
const DataMigrationPage = lazy(() =>
  import('../components/platform/data-migration/DataMigrationPage').then((m) => ({
    default: m.DataMigrationPage,
  })),
)
const MspDataCleanupMain = lazy(
  () => import('../components/msp/manage-data-cleanup/MspDataCleanupMain'),
)

// Users & Roles
const UsersAndRoles = lazy(() => import('../components/company/users-and-roles'))
const MspUsersAndRoles = lazy(() => import('../components/msp/users-and-roles'))
const GeneralUsers = lazy(() => import('../components/platform/users-and-roles/GeneralUsers'))
const GeneralAdmins = lazy(() => import('../components/platform/users-and-roles/GeneralAdmins'))
const AddOrEditGa = lazy(() => import('../components/platform/users-and-roles/AddOrEditGa'))

// Bill Codes
const AddBillCodePage = lazy(() =>
  import('../pages/AddBillCodePage').then((m) => ({default: m.AddBillCodePage})),
)
const CompanyAdminBillCodePage = lazy(() =>
  import('../pages/CompanyAdminBillCodePage').then((m) => ({default: m.CompanyAdminBillCodePage})),
)

// Candidate Distribution
const CandidateDistributionPageWrapper = lazy(() =>
  import('../components/ui/candidate-distribution-field/CandidateDistributionPageWrapper').then(
    (m) => ({default: m.CandidateDistributionPageWrapper}),
  ),
)
const CandidateDistributionPage = lazy(() =>
  import('../components/ui/candidate-distribution-field/CandidateDistributionPage').then((m) => ({
    default: m.CandidateDistributionPage,
  })),
)

// Company-specific
const ManagePositions = lazy(() => import('../components/company/positions/ManagePositions'))
const ManageTemplates = lazy(() => import('../components/company/manage-templates/ManageTemplates'))
const InvoicesPage = lazy(() =>
  import('../components/company/invoices/InvoicesPage').then((m) => ({default: m.InvoicesPage})),
)
const TimesheetPendingView = lazy(() =>
  import('../components/company/timesheets/TimesheetPendingView').then((m) => ({
    default: m.TimesheetPendingView,
  })),
)

// MSP-specific
const ManageClientPositions = lazy(
  () => import('../components/msp/manage-positions/ManageClientPositions'),
)
const MspManageTemplates = lazy(() => import('../components/msp/manage-templates/ManageTemplates'))
const ManagedSitesPage = lazy(() =>
  import('../components/platform/msp/ManagedSitesPage').then((m) => ({
    default: m.ManagedSitesPage,
  })),
)

// Settings
const EventCategoriesSettingsPage = lazy(() =>
  import('../components/platform/settings/event-categories').then((m) => ({
    default: m.EventCategoriesSettingsPage,
  })),
)
const ComplianceFieldsSettingsPage = lazy(() =>
  import('../components/platform/settings/compliance-fields').then((m) => ({
    default: m.ComplianceFieldsSettingsPage,
  })),
)
const SettingsESignFilesPage = lazy(() =>
  import('../components/platform/settings/documents-files').then((m) => ({
    default: m.SettingsESignFilesPage,
  })),
)
const SettingsESignSignaturesPage = lazy(() =>
  import('../components/platform/settings/documents-signatures').then((m) => ({
    default: m.SettingsESignSignaturesPage,
  })),
)
const ZoomSetupSettingsPage = lazy(() =>
  import('../components/platform/settings/zoom-setup').then((m) => ({
    default: m.ZoomSetupSettingsPage,
  })),
)
const EVerifySettingsPage = lazy(() =>
  import('../components/platform/settings/e-verify').then((m) => ({
    default: m.EVerifySettingsPage,
  })),
)
const HelpSettingsPage = lazy(() =>
  import('../components/platform/settings/help').then((m) => ({
    default: m.HelpSettingsPage,
  })),
)
const FaqAdminSettingsPage = lazy(() =>
  import('../components/platform/settings/faq-admin').then((m) => ({
    default: m.FaqAdminSettingsPage,
  })),
)
const InvoiceTemplatesSettingsPage = lazy(() =>
  import('../components/platform/settings/invoice-templates').then((m) => ({
    default: m.InvoiceTemplatesSettingsPage,
  })),
)

// Other
const AccountDetails = lazy(() => import('../components/platform/onboarding/AccountDetails'))

// Page Wrapper
const PageWrapper = lazy(() =>
  import('../components/ui/layout/PageWrapper').then((m) => ({default: m.PageWrapper})),
)

// Reports
const ReportsPage = lazy(() =>
  import('../pages/reports/ReportsPage').then((m) => ({default: m.ReportsPage})),
)
const ReportBuilderPage = lazy(() =>
  import('../pages/reports/builder/ReportBuilderPage').then((m) => ({
    default: m.ReportBuilderPage,
  })),
)

// Roles & Permissions
const RolesPermissionsPage = lazy(() =>
  import('../components/Roles_And_Permissions/pages/RolesPermissionsPage').then((m) => ({
    default: m.RolesPermissionsPage,
  })),
)

// ============================================================================
// REDIRECT COMPONENTS
// These are simple components that handle navigation based on user context
// ============================================================================

// Agency Profile Redirect - redirects to the logged-in user's agency profile
const AgencyProfileRedirectComponent: React.FC = () => {
  const {loggedInAgencyId} = useAppSelector((state) => state.root)
  if (loggedInAgencyId) {
    return <Navigate to={`/manage-agencies/${loggedInAgencyId}/profile`} replace />
  }
  return <div>Error: No agency ID found</div>
}

// Company Profile Redirect - redirects to the logged-in user's company profile
const CompanyProfileRedirectComponent: React.FC = () => {
  const {loggedInCompanyId} = useAppSelector((state) => state.root)
  if (loggedInCompanyId) {
    return <Navigate to={`/manage-companies/${loggedInCompanyId}/profile`} replace />
  }
  return <div>Error: No company ID found</div>
}

// Add New User page - lazy loaded
const AddNewUserPage = lazy(() =>
  import('../components/Roles_And_Permissions/pages/AddNewUserPage').then((m) => ({
    default: m.AddNewUserPage,
  })),
)

// Edit User page - lazy loaded
const EditUserPage = lazy(() =>
  import('../components/Roles_And_Permissions/pages/EditUserPage').then((m) => ({
    default: m.EditUserPage,
  })),
)

// Staffer performance detail - lazy loaded
const StafferMetricsPage = lazy(() =>
  import('../components/platform/agency/staffer-metrics/StafferMetricsPage').then((m) => ({
    default: m.StafferMetricsPage,
  })),
)

// Role pages - lazy loaded
const AddRolePage = lazy(() =>
  import('../components/Roles_And_Permissions/pages/AddRolePage').then((m) => ({
    default: m.AddRolePage,
  })),
)

const EditRolePage = lazy(() =>
  import('../components/Roles_And_Permissions/pages/EditRolePage').then((m) => ({
    default: m.EditRolePage,
  })),
)

const RoleDetailPage = lazy(() =>
  import('../components/Roles_And_Permissions/pages/RoleDetailPage').then((m) => ({
    default: m.RoleDetailPage,
  })),
)

// RoleUsersPage removed - now handled by RoleDetailPage with ?view=users query param

const GlobalRolesPermissionsPage = lazy(() =>
  import('../components/Roles_And_Permissions/pages/GlobalRolesPermissionsPage').then((m) => ({
    default: m.GlobalRolesPermissionsPage,
  })),
)

// Tasks / Notifications
const TasksPage = lazy(() => import('../components/tasks/TasksPage'))
const NotificationsPage = lazy(() => import('../components/notifications/NotificationsPage'))

// eSign / Documents
const ESignFilesMain = lazy(() => import('../components/agency/esign/files/FilesMain'))
const ESignSignaturesMain = lazy(
  () => import('../components/agency/esign/signatures/SignaturesMain'),
)
const SigningView = lazy(() => import('../components/shared/Signing/SigningView'))
const ESignMySignaturesPage = lazy(
  () => import('../components/shared/MySignatures/MySignaturesPage'),
)

// ============================================================================
// Candidate Self-Service
const CandidateDashboard = lazy(() => import('../components/candidate/CandidateDashboard'))
const CandidateProfile = lazy(() => import('../components/candidate/CandidateProfile'))
const CandidatePaychecks = lazy(() => import('../components/candidate/CandidatePaychecks'))
const CandidateDocuments = lazy(() => import('../components/candidate/CandidateDocuments'))

// Announcements
const AnnouncementListPage = lazy(() =>
  import('../components/announcements').then((m) => ({default: m.AnnouncementListPage})),
)
const AnnouncementFormPage = lazy(() =>
  import('../components/announcements').then((m) => ({default: m.AnnouncementFormPage})),
)
const AnnouncementDetailPage = lazy(() =>
  import('../components/announcements').then((m) => ({default: m.AnnouncementDetailPage})),
)
const CandidateAnnouncementsPage = lazy(() =>
  import('../components/announcements').then((m) => ({
    default: m.CandidateAnnouncementsPage,
  })),
)
const CandidateAnnouncementDetailPage = lazy(() =>
  import('../components/announcements').then((m) => ({
    default: m.CandidateAnnouncementDetailPage,
  })),
)

// LOADING FALLBACK
// ============================================================================

const LoadingFallback = () => (
  <div style={{display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh'}}>
    <LoadingBoundary isLoading={true} />
  </div>
)

// ============================================================================
// COMPONENT MAP
// ============================================================================

/**
 * Maps component names from routeConfig to actual React components
 * Lazy-loaded components for code splitting
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const LAZY_COMPONENT_MAP: Record<string, React.LazyExoticComponent<React.ComponentType<any>>> = {
  // Dashboards
  Dashboard,
  AgencyDashboard,
  CompanyDashboard,
  // Company Management
  ManageCompaniesPage,
  CompanyManagementPage,
  CompanyDetailPage,
  AddNewPositionPage,
  EditPositionPage,
  AddPositionTemplatePage,
  // Agency Management
  ManageAgenciesPage,
  AgencyManagementPage,
  OnboardingAgency,
  OnboardingCompany,
  // Global
  CompensationCodesPage,
  WCCatalogsPage,
  CatalogCompensationCodesPage,
  PaycodesPage,
  PaycodeFormPage,
  AuthoritiesPage,
  TaxManagementPage,
  MetricsDashboardPage,
  ZoomConfigPage,
  LenderAdminPage,
  FinancingSchedulesPage,
  TimeOffPoliciesPage,
  TimeOffCategoryDetailPage,
  CreateTimeOffPolicyPage,
  TimeOffPolicyDetailPage,
  PlaceholderPage,
  ActivityLogsPage,
  // Payroll
  PayrollManagementPage,
  PayrollReportsPage,
  BillingSettingsPage,
  FinancePage,
  // Service Orders
  NewServiceOrderFormPage,
  ServiceOrderDetailsPage,
  ServiceOrderSummary,
  AgencyManageOrdersMain,
  AgencyPendingOrders,
  CompletedOrdersMain,
  DeletedOrdersMain,
  ManageOrders,
  OrderDetails,
  CompanyCompletedOrdersMain,
  CompanyCandidateViewMain,
  // Job Orders
  MspAgencyJobOrderMain,
  MspJobOrdersDetailsMain,
  JobOrdersDetailsSummaryMain,
  JobOrderFromPrefill,
  SubOrderDetailsPage,
  // Candidates & Prefill
  Candidates,
  PrefillMain,
  AddOrEditPrefill,
  // Requests
  RequestsPage,
  // Data
  ImportWizardMain,
  DataMigrationPage,
  MspDataCleanupMain,
  // Users & Roles
  UsersAndRoles,
  MspUsersAndRoles,
  GeneralUsers,
  GeneralAdmins,
  AddOrEditGa,
  // Bill Codes
  AddBillCodePage,
  CompanyAdminBillCodePage,
  // Candidate Distribution
  CandidateDistributionPageWrapper,
  CandidateDistributionPage,
  // Company
  ManagePositions,
  ManageTemplates,
  InvoicesPage,
  TimesheetPendingView,
  // MSP
  ManageClientPositions,
  MspManageTemplates,
  ManagedSitesPage,
  // Settings
  EventCategoriesSettingsPage,
  ComplianceFieldsSettingsPage,
  SettingsESignFilesPage,
  SettingsESignSignaturesPage,
  ZoomSetupSettingsPage,
  EVerifySettingsPage,
  HelpSettingsPage,
  FaqAdminSettingsPage,
  InvoiceTemplatesSettingsPage,
  // Other
  AccountDetails,
  PageWrapper,
  // Reports
  ReportsPage,
  ReportBuilderPage,
  // Roles & Permissions
  RolesPermissionsPage,
  UsersRolesPage: RolesPermissionsPage, // Alias to match routeConfig
  AddNewUserPage,
  EditUserPage,
  StafferMetricsPage,
  AddRolePage,
  EditRolePage,
  RoleDetailPage,
  GlobalRolesPermissionsPage,
  // Tasks / Notifications
  TasksPage,
  NotificationsPage,
  // eSign / Documents
  ESignFilesMain,
  ESignSignaturesMain,
  SigningView,
  MySignaturesPage: ESignMySignaturesPage,

  // Candidate Self-Service
  CandidateDashboard,
  CandidateProfile,
  CandidatePaychecks,
  CandidateDocuments,
  // Announcements
  AnnouncementListPage,
  AnnouncementFormPage,
  AnnouncementDetailPage,
  CandidateAnnouncementsPage,
  CandidateAnnouncementDetailPage,
}

/**
 * Maps component names to regular (non-lazy) React components
 * Used for redirect components and placeholders
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const REGULAR_COMPONENT_MAP: Record<string, React.ComponentType<any>> = {
  AgencyProfileRedirect: AgencyProfileRedirectComponent,
  CompanyProfileRedirect: CompanyProfileRedirectComponent,
  SettingsRedirect: SettingsRedirectComponent,
}

// ============================================================================
// ROUTE RENDERER
// ============================================================================

interface RouteRendererProps {
  route: RouteConfig
  userRole: TCognitoRole | null
}

/**
 * Renders a single route with moduleKey permission checking
 * Uses modulePermissions from backendRolePermissions to check canView
 */
const GuardedRoute: React.FC<RouteRendererProps> = ({route, userRole}) => {
  const backendPermissions = useAppSelector((state) => state.root.backendRolePermissions)

  // Handle Navigate (redirect) routes
  if (route.component === 'Navigate' && route.meta?.redirectTo) {
    return <Navigate to={route.meta.redirectTo} replace />
  }

  // Check lazy components first, then regular components
  const LazyComponent = LAZY_COMPONENT_MAP[route.component]
  const RegularComponent = REGULAR_COMPONENT_MAP[route.component]

  if (!LazyComponent && !RegularComponent) {
    console.warn(`Component "${route.component}" not found in COMPONENT_MAP`)
    return null
  }

  // Platform-admin-only screens (their API answers 403 to everyone else), whatever the module
  // permissions say.
  if (!passesPlatformAdminGate(route, backendPermissions?.isPlatform)) {
    return <Navigate to="/unauthorized" replace />
  }

  // If route has moduleKey requirement, check modulePermissions
  // Note: Client-side permission checks are for UX only - API validates all requests server-side
  if (route.moduleKey) {
    // TODO(feature-flag): dashboardMetrics — remove when flag is deleted
    if (route.id === 'platform-dashboard' && !isEnabled('dashboardMetrics')) {
      return <Navigate to="manage-companies" replace />
    }

    // Use Redux state (persisted by redux-persist, rehydrated on refresh). The rule, Settings pages
    // included, lives in routeAccess.ts.
    if (isRouteDeniedByPermissions(route, backendPermissions)) {
      return <Navigate to="/unauthorized" replace />
    }
    // If backendPermissions is null, allow access (Settings pages excepted, see routeAccess.ts)
    // The component itself should handle loading state, and API will reject unauthorized requests
  }

  // Render the component
  if (LazyComponent) {
    return (
      <ChunkErrorBoundary>
        <Suspense fallback={<LoadingFallback />}>
          <LazyComponent />
        </Suspense>
      </ChunkErrorBoundary>
    )
  }

  if (RegularComponent) {
    return <RegularComponent />
  }

  return null
}

/**
 * Renders a single route. The two pages a job order invitation email links to first apply the
 * link's `?agencyId=` (STFFLG-4184 Slice 5, BranchFromLinkGate), so GuardedRoute's permission check
 * and the page read the branch the link opens in. Exported for its wiring test
 * (__tests__/AppRoutes.branchFromLink.test.tsx), which fails if the gate is dropped from here.
 */
export const RouteRenderer: React.FC<RouteRendererProps> = ({route, userRole}) =>
  BRANCH_LINK_ROUTE_IDS.has(route.id) ? (
    <BranchFromLinkGate>
      <GuardedRoute route={route} userRole={userRole} />
    </BranchFromLinkGate>
  ) : (
    <GuardedRoute route={route} userRole={userRole} />
  )

// ============================================================================
// LAYOUT ROUTES COMPONENT
// ============================================================================

interface LayoutRoutesProps {
  config: LayoutConfig
}

/**
 * Renders all routes for a specific layout
 */
const LayoutRoutes: React.FC<LayoutRoutesProps> = ({config}) => {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const {currentUser} = useAppSelector((state) => state.root)
  const [getUserByEmail] = useLazyGetUserByEmailQuery()
  const [signout] = useSignoutMutation()
  const {showError} = useGlobalSnackbar()

  // EULA/Terms acceptance check
  const [eulaModalVisible, setEulaModalVisible] = useState(false)

  // Check if user has agreed to terms on component mount
  useEffect(() => {
    if (
      (currentUser && !currentUser.agreedToTermsDate) ||
      currentUser?.agreedToTermsDate === null
    ) {
      setEulaModalVisible(true)
    }
  }, [currentUser])

  // Handle EULA acceptance
  const handleEulaAccept = async () => {
    try {
      await userApi.updateAgreedToTermsDate(new Date().toISOString())

      // Refetch user to update Redux state with new agreedToTermsDate
      if (currentUser?.emailAddress) {
        const updatedUser = await getUserByEmail(currentUser.emailAddress).unwrap()
        dispatch(updateCurrentUser(updatedUser))
      }

      setEulaModalVisible(false)
    } catch (error: unknown) {
      showError(error)
    }
  }

  // STFFLG-3872: declining (or dismissing) the EULA must actually LOG THE USER OUT.
  // Previously this only navigate('/signin') while leaving currentUser+token in
  // Redux, so PrivateRoute still authorized and the user could navigate right back
  // in. Reuse the canonical signout (same as UserMenu.handleSignOut): the signout
  // mutation clears local/session storage (honoring isIopSession) and its
  // matchFulfilled nulls currentUser, so PrivateRoute bounces to /signin.
  const handleEulaDecline = async () => {
    setEulaModalVisible(false)
    try {
      dispatch(notificationsApi.util.resetApiState())
      if (currentUser?.emailAddress) {
        await signout({email: currentUser.emailAddress}).unwrap()
      }
    } catch (error: unknown) {
      showError(error)
    } finally {
      navigate('/signin')
    }
  }

  // Get user role from currentUser (cast to TCognitoRole)
  const userRole = (currentUser?.roles?.[0] as TCognitoRole) || null

  return (
    <Layout variant={config.variant}>
      <S.Container>
        <Routes>
          {config.routes.map((route) => (
            <Route
              key={route.id}
              path={route.path}
              index={route.meta?.index}
              element={<RouteRenderer route={route} userRole={userRole} />}
            />
          ))}
          {/*
            STFFLG-3995: without this, an unmatched path rendered `null` inside the layout — nav
            chrome above an empty white content area, with no error anywhere. A redirect missing a
            leading slash produced exactly that and took a bug report and a video to find. The
            catch-all makes the next one say what it tried to open.
          */}
          <Route path="*" element={<RouteNotFound />} />
        </Routes>
      </S.Container>

      {/* EULA Dialog - Shows when user hasn't agreed to terms */}
      <EulaDialog
        isModalOpen={eulaModalVisible}
        handleClick={async (accepted: boolean) => {
          if (accepted) {
            await handleEulaAccept()
          } else {
            await handleEulaDecline()
          }
        }}
        onModalClose={handleEulaDecline}
      />
    </Layout>
  )
}

// ============================================================================
// MAIN APP ROUTES COMPONENT
// ============================================================================

/**
 * Determines the layout variant from the role's category
 * This is the primary method for custom roles that don't follow naming patterns
 */
const getVariantFromCategory = (
  category: 'Platform' | 'MSP' | 'Agency' | 'Company' | 'Candidate' | undefined,
): LayoutVariant | null => {
  switch (category) {
    case 'Platform':
      return 'platform'
    case 'MSP':
      return 'msp'
    case 'Agency':
      return 'agency'
    case 'Company':
      return 'company'
    case 'Candidate':
      return 'candidate'
    default:
      return null // No category available, fall back to role name matching
  }
}

/**
 * Determines the layout variant from the user's role name
 * Used as fallback when category is not available
 * URLs no longer contain role prefixes - variant is determined from user data
 */
const getVariantFromRoleName = (roleName: string | undefined): LayoutVariant => {
  if (!roleName) return 'platform' // Default fallback

  const lower = roleName.toLowerCase()

  // Platform admin roles (full platform access)
  if (lower === 'platformadmin') {
    return 'platform'
  }

  // General admin (cross-agency admin with limited modules)
  if (lower === 'general_admin') {
    return 'general-admin'
  }

  // Main agency admin (MSP parent agency)
  if (lower === 'mainagencyadmin') {
    return 'main-agency'
  }

  // MSP specific agency admin
  if (lower === 'mspadmin' || lower === 'mspagencyadmin' || lower === 'mspacencyadmin') {
    return 'msp'
  }

  // Regular agency admin and other agency roles
  // Note: agencyadmin can be for both MSP and non-MSP agencies. That is the agency's kind, not
  // the role's category: an MSP-CATEGORY role gets 'msp' from getVariantFromCategory above and
  // never reaches this fallback.
  if (lower === 'agencyadmin' || lower.includes('agency')) {
    return 'agency'
  }

  // Company roles
  if (lower === 'companyadmin' || lower === 'companyuser') {
    return 'company'
  }

  // Candidate self-service
  if (lower === 'candidate') {
    return 'candidate'
  }

  // Default to platform
  return 'platform'
}

/**
 * Gets layout config by variant
 */
const getLayoutConfigByVariant = (variant: LayoutVariant): LayoutConfig | undefined => {
  switch (variant) {
    case 'platform':
      return PLATFORM_ADMIN_CONFIG
    case 'general-admin':
      // General admin uses platform routes but has its own nav menu (generalAdminList)
      return {...PLATFORM_ADMIN_CONFIG, variant: 'general-admin'}
    case 'main-agency':
      return MAIN_AGENCY_ADMIN_CONFIG
    case 'msp':
      return MSP_AGENCY_ADMIN_CONFIG
    case 'agency':
      return AGENCY_ADMIN_CONFIG
    case 'company':
      return COMPANY_ADMIN_CONFIG
    case 'candidate':
      return CANDIDATE_CONFIG
    default:
      return undefined
  }
}

/**
 * Main unified routes component
 *
 * Variant is now determined from the user's role stored in Redux,
 * NOT from URL prefixes. URLs are role-agnostic.
 *
 * Examples:
 * - /manage-companies (same URL for all roles)
 * - /users-roles (same URL for all roles)
 * - What the user sees is determined by their permissions, not the URL
 */
export const AppRoutes: React.FC = () => {
  const {currentUser, backendRolePermissions} = useAppSelector((state) => state.root)

  // Get the user's primary role (first role in array)
  const userRole = currentUser?.roles?.[0]

  // Determine variant: first try category from backend, then fall back to role name pattern
  // This ensures custom roles (like "testing_custom_role") use the correct layout
  // based on their category (Agency, Company, etc.) rather than role name patterns
  const categoryVariant = getVariantFromCategory(backendRolePermissions?.category)
  const variant = categoryVariant ?? getVariantFromRoleName(userRole)

  const config = getLayoutConfigByVariant(variant)

  if (!config) {
    reportCatchError(
      `No layout config found for variant: ${variant}`,
      new Error('Missing layout config'),
    )
    return <Navigate to="/signin" replace />
  }

  return <LayoutRoutes config={config} />
}

export default AppRoutes
